/**
 * API client.
 *
 * Two deliberate properties:
 *
 *  1. No token is ever stored in JavaScript. Authentication rides on an
 *     HttpOnly cookie the browser attaches automatically, which is why every
 *     request sets `credentials: 'include'`. Nothing here touches
 *     localStorage or sessionStorage.
 *
 *  2. Unsafe methods echo the CSRF cookie in `x-csrf-token`. That cookie is
 *     readable by design — it is the client half of a double-submit check,
 *     not a credential on its own.
 */

/** Port the backend API listens on during development. */
const DEV_API_PORT = import.meta.env.VITE_API_PORT || '3000';

/**
 * Base URL of the admin API.
 *
 * When `VITE_API_URL` is not set, the host is taken from the page itself
 * rather than hardcoded. That matters for cookies: `SameSite=Lax` is scoped
 * by *site*, and `localhost` and `127.0.0.1` are different sites even though
 * they resolve to the same machine — so a page on `localhost` talking to an
 * API on `127.0.0.1` would never get its session cookie back. Ports are not
 * part of a site, so deriving the hostname keeps the two same-site whichever
 * address the operator opens.
 */
function resolveApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL;
  if (configured) return configured.replace(/\/$/, '');
  if (typeof window === 'undefined') return `http://127.0.0.1:${DEV_API_PORT}`;
  return `${window.location.protocol}//${window.location.hostname}:${DEV_API_PORT}`;
}

export const API_BASE_URL = resolveApiBaseUrl();

const CSRF_COOKIE = 'insider_csrf';

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the session is gone and the user must sign in again. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/** Listeners notified when the server reports the session is no longer valid. */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

const UNSAFE = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** Suppress the global unauthorized handler (used by the login form). */
  skipAuthRedirect?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };

  if (options.body !== undefined) headers['content-type'] = 'application/json';

  if (UNSAFE.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers['x-csrf-token'] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    // Network-level failure: the API is unreachable, not refusing us.
    throw new ApiError(0, 'Cannot reach the API. Is the backend running?');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const body = (payload ?? {}) as { error?: string; details?: Record<string, string[]> };
    const error = new ApiError(
      response.status,
      body.error ?? `Request failed (${response.status})`,
      body.details,
    );
    if (error.isUnauthorized && !options.skipAuthRedirect) onUnauthorized?.();
    throw error;
  }

  return payload as T;
}

/** Builds a query string, dropping empty values so URLs stay clean. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
